import { useEffect, useState, useCallback } from "react";
import { useUserManagementStore } from "src/hooks/stores";
import { useTranslation } from "react-i18next";
import { User } from "src/interfaces/user";
import { useNavigate } from "react-router-dom";
import { Grid } from "@vfuk/core-grid";
import Spacing from "@vfuk/core-spacing";
import manageAccessBanner from "@img/manageAccessBanner.png";
import InfiniteScroll from "react-infinite-scroll-component";
import Container from "@vfuk/core-container";
import {
  SectionBanner,
  TableHeaderRow,
  TableRowLoader,
  NoResultsRow,
  Crumbs,
  TableActionButtons,
  Stack,
  useDebounce,
  TableControls,
} from "@ucchub/ui";
import DashShell from "@components/common/DashShell";
import breadCrumbs from "@json/common/crumbs.json";
import ManageAccessLayout from "@components/layout/ManageAccessLayout";
import UserRow from "./UserRow/UserRow";
import ButtonWithIcon from "@vfuk/core-button-with-icon";
import UsersFilterRow from "./UsersFilterRow/UsersFilterRow";
import { FilterValues } from "./UserManagement.types";
import useCurrentUser from "@hooks/useCurrentUser";

const getFilterCount = (filterData: FilterValues | undefined) => {
  if (!filterData) {
    return 0;
  }
  const filterDataNew = {
    ...filterData,
  };

  const filterCount = Object.values(filterDataNew).reduce(
    (accumulator, item) => accumulator + (item ? 1 : 0),
    0
  );

  return filterCount;
};

const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const clearUsersData = useUserManagementStore((state) => state.clearUsers);
  const { vFAdmin } = useCurrentUser();
  useEffect(() => {
    return () => {
      clearUsersData();
    };
  }, [clearUsersData]);

  const fetchUsers = useUserManagementStore((state) => state.getUsers.call);
  const usersLoading = useUserManagementStore(
    (state) => state.getUsers.loading
  );
  const userError = useUserManagementStore((state) => state.getUsers.error);
  const userActions = useUserManagementStore((state) => state.getUsers.actions);

  const [users, setUsers] = useState<User[]>([]);
  const paginationToken = useUserManagementStore(
    (state) => state.paginationKey
  );

  const [filterData, setFilterData] = useState<FilterValues>();

  const addSelectAllUsers = useUserManagementStore(
    (state) => state.addSelectAllUsers
  );

  const usersSelected =
    useUserManagementStore((state) => state.selectedUsers).length > 0;

  const usersSelectedAll =
    useUserManagementStore((state) => state.selectedUsers).length ===
      users.length && usersSelected;

  const [tableSelectAll, setTableSelectAll] = useState(false);
  const clearAllSelectedUsers = useUserManagementStore(
    (state) => state.clearAllSelectedUsers
  );
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 200);

  const changeSelectAll = (checked: boolean) => {
    setTableSelectAll(checked);
    clearAllSelectedUsers();
    if (checked) addSelectAllUsers(users);
  };

  const getUsersData = useCallback(async () => {
    const res: User[] = await fetchUsers(undefined, tableSelectAll, filterData);
    const sdata = Array.from(res || []);
    setUsers((current) => [...current, ...sdata]);
  }, [filterData, fetchUsers, tableSelectAll]);

  const handleAddUserClicked = () => {
    navigate("/users/add");
  };

  const renderUserRows = useCallback(() => {
    if (usersLoading && paginationToken === "") {
      return (
        <TableRowLoader
          rowCount={1}
          colCount={4}
          colWidths={["100%", "50%", "50%", "40%"]}
        />
      );
    }

    if (!usersLoading && users.length === 0) {
      return (
        <NoResultsRow
          text={userError ? userError : t("shared:users:no_users")}
        />
      );
    }

    return users.map((user, i) => (
      <UserRow
        key={i}
        user={user}
        userActions={userActions}
        isVfAdmin={vFAdmin}
      />
    ));
  }, [vFAdmin, userActions, userError, usersLoading, users, t]);

  const fetchAndAppendUsers = useCallback(async () => {
    const usersData = await fetchUsers(
      debouncedSearchValue,
      tableSelectAll,
      filterData
    );
    const newUsers: User[] = usersData || [];
    setUsers((prevUsers) => [...prevUsers, ...newUsers]);
  }, [fetchUsers, filterData, debouncedSearchValue, tableSelectAll]);

  // Filter users every time the user types in the search field
  useEffect(() => {
    if (debouncedSearchValue !== "") {
      clearUsersData();
      (async () => {
        const res = await fetchUsers(
          debouncedSearchValue,
          undefined,
          filterData
        );
        setUsers(res || []);
      })();
    } else {
      (async () => {
        const res = await fetchUsers(undefined, undefined, filterData);
        setUsers(res || []);
      })();
    }
  }, [clearUsersData, fetchUsers, filterData, debouncedSearchValue]);

  const fetchMoreData = () => {
    if (paginationToken !== "" && searchValue === "") getUsersData();
    else fetchAndAppendUsers();
  };

  useEffect(() => {
    if (usersSelectedAll && users.length > 0) changeSelectAll(usersSelectedAll);
    else setTableSelectAll(usersSelectedAll);
  }, [usersSelectedAll]);

  const [openFilter, setOpenFilter] = useState(false);

  const getTableHeaders = () => {
    const HeaderRow = ["Email", "Name", "Status", "Role"];
    if (vFAdmin) HeaderRow.splice(2, 0, "Organisation");
    return HeaderRow;
  };

  return (
    <DashShell>
      <div>
        <Crumbs items={breadCrumbs.users} />
        <SectionBanner
          heading={t("shared:users:title")}
          subtitle={t("shared:users:subtitle") || "User details and roles"}
          image={manageAccessBanner}
          iconName="users"
        />
        <ManageAccessLayout activeTab="/users" />
        <Spacing spacingLevel={{ bottom: 7 }}>
          <Container appearance="secondary">
            <Grid maxWidth="full">
              <Spacing spacingLevel={{ top: 0 }}>
                <Stack justifyContent="space-between">
                  <Spacing spacingLevel={{ left: 1 }}>
                    <TableControls
                      id="users"
                      addButton
                      search={{
                        placeholder:
                          t("shared:users:search_info") || "Search...",
                        value: searchValue,
                        valueChanged: setSearchValue,
                      }}
                      filterButton={
                        <ButtonWithIcon
                          icon={{
                            name: openFilter ? "chevron-up" : "chevron-down",
                            justify: "right",
                          }}
                          text={`${getFilterCount(filterData)} ${t(
                            "shared:users:filters.title"
                          )}`}
                          appearance="alt2"
                          onClick={() => setOpenFilter(!openFilter)}
                        />
                      }
                    />
                  </Spacing>
                  <Stack>
                    <Spacing spacingLevel={{ right: 2 }}>
                      <TableActionButtons
                        actions={[
                          {
                            isDisabled: !userActions.add,
                            shouldRender: !!userActions.add,
                            text: userActions?.add?.label || "",
                            icon: {
                              name: userActions?.add?.icon || "",
                              justify: "left",
                            },
                            onClick: handleAddUserClicked,
                          },
                        ]}
                      />
                    </Spacing>
                  </Stack>
                </Stack>
              </Spacing>

              <UsersFilterRow
                onFilterClosed={() => setOpenFilter(false)}
                showStatusFilter={false}
                isOpen={openFilter}
                onFilterApplied={(filters) => {
                  clearUsersData();
                  setFilterData(filters);
                }}
                isVfAdmin={vFAdmin}
              />
              <div role="table" aria-label="Portal users">
                <TableHeaderRow headers={getTableHeaders()} />
                <InfiniteScroll
                  dataLength={users.length}
                  next={fetchMoreData}
                  hasMore={paginationToken !== ""}
                  className="infiniteScroll-overflow"
                  loader={
                    <TableRowLoader
                      rowCount={1}
                      colCount={5}
                      colWidths={["100%", "50%", "50%", "40%"]}
                    />
                  }
                >
                  {renderUserRows()}
                </InfiniteScroll>
              </div>
            </Grid>
          </Container>
        </Spacing>
      </div>
    </DashShell>
  );
};

export default Users;
